import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const DocumentGrid = ({ 
  documents, 
  viewMode, 
  onDocumentSelect, 
  onDocumentDelete, 
  onDocumentEdit,
  onDocumentRename,
  selectedDocuments,
  onSelectionChange 
}) => {
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const getFileIcon = (type) => {
    if (!type) return 'File';
    const iconMap = {
      'pdf': 'FileText',
      'doc': 'FileText',
      'docx': 'FileText',
      'xls': 'Sheet',
      'xlsx': 'Sheet',
      'jpg': 'Image',
      'jpeg': 'Image',
      'png': 'Image',
      'txt': 'FileText'
    };
    return iconMap?.[type?.toLowerCase()] || 'File';
  };

  const getFileTypeColor = (type) => {
    if (!type) return 'text-gray-600';
    const colorMap = {
      'pdf': 'text-red-600',
      'doc': 'text-blue-600',
      'docx': 'text-blue-600',
      'xls': 'text-green-600',
      'xlsx': 'text-green-600',
      'jpg': 'text-purple-600',
      'jpeg': 'text-purple-600',
      'png': 'text-purple-600',
      'txt': 'text-gray-600'
    };
    return colorMap?.[type?.toLowerCase()] || 'text-gray-600';
  };

  const formatFileSize = (bytes) => {
    // Handle null, undefined, or invalid values
    if (!bytes || bytes === 0 || isNaN(bytes)) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const formatDate = (dateString) => {
    // Handle null, undefined, or invalid dates
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      if (isNaN(date?.getTime())) return 'Invalid Date';
      return date?.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const sortedDocuments = [...documents]?.sort((a, b) => {
    let aValue = a?.[sortBy];
    let bValue = b?.[sortBy];
    
    if (sortBy === 'uploadDate') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleDocumentClick = (document) => {
    if (selectedDocuments?.length > 0) {
      // Multi-select mode - toggle selection
      toggleDocumentSelection(document);
    } else {
      onDocumentSelect(document);
    }
  };

  // Add this new function to handle checkbox selection
  const toggleDocumentSelection = (document) => {
    const isSelected = selectedDocuments?.some(doc => doc?.id === document?.id);
    if (isSelected) {
      onSelectionChange(selectedDocuments?.filter(doc => doc?.id !== document?.id));
    } else {
      onSelectionChange([...selectedDocuments, document]);
    }
  };

  // Add validation for document edit
  const handleDocumentEditClick = (e, document) => {
    e?.stopPropagation();
    
    // Validate document has required ID before navigating
    if (!document?.id) {
      console.error('Cannot edit document: Invalid document ID');
      return;
    }
    
    onDocumentEdit(document);
  };

  // Add validation for document delete
  const handleDocumentDeleteClick = (e, document) => {
    e?.stopPropagation();
    
    // Comprehensive validation before deletion
    if (!document) {
      console.error('Cannot delete document: No document provided');
      return;
    }
    
    if (!document?.id) {
      console.error('Cannot delete document: Invalid document ID', document);
      return;
    }
    
    // Ensure document has name for display in confirmation
    if (!document?.name) {
      console.error('Cannot delete document: Document missing name property', document);
      return;
    }
    
    console.log('Deleting document:', { id: document?.id, name: document?.name });
    onDocumentDelete(document);
  };

  // Add validation for document rename
  const handleDocumentRenameClick = (e, document) => {
    e?.stopPropagation();
    
    // Validate document before renaming
    if (!document?.id) {
      console.error('Cannot rename document: Invalid document ID');
      return;
    }
    
    onDocumentRename?.(document);
  };

  const isDocumentSelected = (document) => {
    return selectedDocuments?.some(doc => doc?.id === document?.id);
  };

  if (viewMode === 'list') {
    return (
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        {/* List Header */}
        <div className="grid grid-cols-12 gap-4 p-4 bg-muted/50 border-b border-border text-sm font-medium text-muted-foreground">
          <div className="col-span-1">
            <input
              type="checkbox"
              className="rounded border-border"
              checked={selectedDocuments?.length === documents?.length && documents?.length > 0}
              onChange={(e) => {
                if (e?.target?.checked) {
                  onSelectionChange(documents);
                } else {
                  onSelectionChange([]);
                }
              }}
            />
          </div>
          <div 
            className="col-span-5 cursor-pointer hover:text-foreground flex items-center gap-1"
            onClick={() => handleSort('name')}
          >
            Name
            {sortBy === 'name' && (
              <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
            )}
          </div>
          <div 
            className="col-span-2 cursor-pointer hover:text-foreground flex items-center gap-1"
            onClick={() => handleSort('size')}
          >
            Size
            {sortBy === 'size' && (
              <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
            )}
          </div>
          <div 
            className="col-span-2 cursor-pointer hover:text-foreground flex items-center gap-1"
            onClick={() => handleSort('uploadDate')}
          >
            Modified
            {sortBy === 'uploadDate' && (
              <Icon name={sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={14} />
            )}
          </div>
          <div className="col-span-2">Actions</div>
        </div>
        {/* List Items */}
        <div className="divide-y divide-border">
          {sortedDocuments?.map((document) => (
            <div
              key={document?.id}
              className={`grid grid-cols-12 gap-4 p-4 hover:bg-muted/50 transition-colors cursor-pointer ${
                isDocumentSelected(document) ? 'bg-primary/5' : ''
              }`}
              onClick={() => handleDocumentClick(document)}
            >
              <div className="col-span-1">
                <input
                  type="checkbox"
                  className="rounded border-border"
                  checked={isDocumentSelected(document)}
                  onChange={(e) => {
                    e?.stopPropagation();
                    toggleDocumentSelection(document);
                  }}
                  onClick={(e) => e?.stopPropagation()}
                />
              </div>
              <div className="col-span-5 flex items-center gap-3">
                <div>
                  <p className="font-medium text-foreground truncate">{document?.name}</p>
                  {document?.description && (
                    <p className="text-sm text-muted-foreground truncate">{document?.description}</p>
                  )}
                </div>
              </div>
              <div className="col-span-2 flex items-center">
                <span className="text-sm text-muted-foreground">{formatFileSize(document?.size)}</span>
              </div>
              <div className="col-span-2 flex items-center">
                <span className="text-sm text-muted-foreground">{formatDate(document?.uploadDate)}</span>
              </div>
              <div className="col-span-2 flex items-center">
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-8 h-8"
                    iconName="Edit2"
                    iconSize={14}
                    onClick={(e) => handleDocumentRenameClick(e, document)}
                    title="Rename document"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-8 h-8"
                    iconName="Edit"
                    iconSize={14}
                    onClick={(e) => handleDocumentEditClick(e, document)}
                    title="Edit document"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-8 h-8 text-error hover:text-error"
                    iconName="Trash2"
                    iconSize={14}
                    onClick={(e) => handleDocumentDeleteClick(e, document)}
                    title="Delete document"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Grid View
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {sortedDocuments?.map((document) => (
        <div
          key={document?.id}
          className={`bg-card border border-border rounded-lg p-4 hover:shadow-md transition-all cursor-pointer ${
            isDocumentSelected(document) ? 'ring-2 ring-primary bg-primary/5' : ''
          }`}
          onClick={() => handleDocumentClick(document)}
        >
          {/* Document Preview */}
          <div className="aspect-square bg-muted rounded-lg mb-3 flex items-center justify-center overflow-hidden">
            {document?.thumbnail ? (
              <Image
                src={document?.thumbnail}
                alt={`Preview of ${document?.name} document showing ${document?.type} file format`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-muted-foreground text-sm font-medium">
                {document?.type?.toUpperCase() || 'FILE'}
              </div>
            )}
          </div>

          {/* Document Info */}
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-medium text-foreground text-sm truncate flex-1">
                {document?.name}
              </h3>
              <input
                type="checkbox"
                className="rounded border-border mt-0.5"
                checked={isDocumentSelected(document)}
                onChange={(e) => {
                  e?.stopPropagation();
                  toggleDocumentSelection(document);
                }}
                onClick={(e) => e?.stopPropagation()}
              />
            </div>
            
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="uppercase font-medium">{document?.type}</span>
              <span>{formatFileSize(document?.size)}</span>
            </div>
            
            <p className="text-xs text-muted-foreground">
              {formatDate(document?.uploadDate)}
            </p>

            {document?.description && (
              <p className="text-xs text-muted-foreground line-clamp-2">
                {document?.description}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-border">
            <Button
              variant="ghost"
              size="icon"
              className="w-8 h-8"
              iconName="Edit2"
              iconSize={14}
              onClick={(e) => handleDocumentRenameClick(e, document)}
              title="Rename document"
            />
            <Button
              variant="ghost"
              size="icon"
              className="w-8 h-8"
              iconName="Edit"
              iconSize={14}
              onClick={(e) => handleDocumentEditClick(e, document)}
              title="Edit document"
            />
            <Button
              variant="ghost"
              size="icon"
              className="w-8 h-8 text-error hover:text-error"
              iconName="Trash2"
              iconSize={14}
              onClick={(e) => handleDocumentDeleteClick(e, document)}
              title="Delete document"
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default DocumentGrid;